import { useCallback, useMemo } from "react";
import { Flex, Text, Button, Box, Spinner } from "@radix-ui/themes";
import { PlusIcon } from "lucide-react";
import { useQuery } from "react-query";
import Select from "@/components/shared/select";
import { FieldGroup } from "../parts/FieldGroup";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import postAnswerStyles from "./PostAnswerActionsConfiguration.module.scss";
import { useParams } from "react-router-dom";
import { useGetSimulator, useGetSimulatorPostAnswerActionsOptions } from "@/v2/entities/simulator/Simulator.api";
import { useEnrichmentApi } from "@/v2/entities/enrichment/Enrichment.api";
import { usePostAnswerActionsHandlers } from "./usePostAnswerActionsHandlers";
import { TagInput } from "./TagInput";
import { CustomFieldRow } from "./CustomFieldRow";
import { CustomFieldDefinition, FieldReferenceOption } from "./PostAnswerActions.model";

export function PostAnswerActionsConfiguration() {
    const { simulatorId } = useParams();
    const enrichmentApi = useEnrichmentApi();

    const { isLoading: isLoadingSimulator } = useGetSimulator(simulatorId);
    const { data: options, isLoading: isLoadingOptions } = useGetSimulatorPostAnswerActionsOptions(simulatorId);

    const { data: enrichments } = useQuery({
        queryKey: ["enrichments-for-post-answer"],
        queryFn: () => enrichmentApi.getAll(),
        select: (data) =>
            data
                .filter((enrichment) => enrichment.isEnabled)
                .map((enrichment) => ({
                    id: enrichment.id,
                    label: enrichment.name,
                })),
    });

    const {
        localConfig,
        updateStatus,
        handleStatusChange,
        handleAddTag,
        handleRemoveTag,
        handleAddCustomField,
        handleUpdateCustomField,
        handleRemoveCustomField,
    } = usePostAnswerActionsHandlers(simulatorId);

    const availableCustomFields = useMemo((): CustomFieldDefinition[] => {
        const fields: CustomFieldDefinition[] = [];

        if (options?.customFields) {
            fields.push(...options.customFields);
        }

        if (enrichments) {
            enrichments.forEach((enrichment) => {
                fields.push({
                    id: enrichment.id,
                    label: `Enrichment: ${enrichment.label}`,
                    type: "text",
                });
            });
        }

        const usedFieldIds = new Set(
            localConfig.customFields?.map((f) => f.fieldId).filter(Boolean) || []
        );

        return fields.filter((f) => !usedFieldIds.has(f.id));
    }, [options?.customFields, enrichments, localConfig.customFields]);

    const getCustomFieldById = useCallback(
        (id: string): CustomFieldDefinition | undefined => {
            const regularField = options?.customFields?.find((f) => f.id === id);
            if (regularField) return regularField;

            const enrichment = enrichments?.find((e) => e.id === id);
            if (enrichment) {
                return {
                    id: enrichment.id,
                    label: `Enrichment: ${enrichment.label}`,
                    type: "text" as const,
                };
            }

            return undefined;
        },
        [options?.customFields, enrichments]
    );

    const enrichmentOptions = useMemo(() => {
        return (
            enrichments?.map((e) => ({
                value: e.id,
                label: e.label,
            })) || []
        );
    }, [enrichments]);

    const fieldReferenceOptions = useMemo((): FieldReferenceOption[] => {
        const ticketFields: FieldReferenceOption[] = [
            { id: "ticket.subject", label: "Subject", category: "ticket" },
            { id: "ticket.description", label: "Description", category: "ticket" },
            { id: "ticket.status", label: "Status", category: "ticket" },
            { id: "ticket.priority", label: "Priority", category: "ticket" },
            { id: "ticket.email", label: "Requester Email", category: "ticket" },
            { id: "ticket.organization", label: "Organization", category: "ticket" },
            { id: "ticket.topic", label: "Topic", category: "ticket" },
        ];

        const customFieldRefs: FieldReferenceOption[] = (options?.customFields ?? []).map((f) => ({
            id: f.id,
            label: f.label,
            category: "customField",
        }));

        return [...ticketFields, ...customFieldRefs];
    }, [options?.customFields]);

    const isLoading = isLoadingSimulator || isLoadingOptions;

    if (isLoading) {
        return (
            <Flex
                justify="center"
                align="center"
                height="100%"
                width="100%"
                direction="column"
                gap="2"
                py="9"
            >
                <Text size="3" weight="medium" color="gray" highContrast>
                    Loading Post-Answer Actions
                </Text>
                <Spinner size="3" />
            </Flex>
        );
    }

    return (
        <Flex direction="column" gap="24px">
            <AutomationConfigurationContentHeader
                title="Post-Answer Actions"
                status={updateStatus}
            />

            <Flex direction="column">
                <Box className={postAnswerStyles.configSection}>
                    <FieldGroup
                        label="Ticket Status"
                        description="Set the ticket status after the AI agent responds."
                    >
                        <Select
                            options={
                                options?.statuses
                                    ?.filter((status) => status.value !== "")
                                    .map((status) => ({
                                        value: status.value,
                                        label: status.label,
                                    })) || []
                            }
                            value={localConfig.status || undefined}
                            onChange={handleStatusChange}
                            placeholder="No change (keep current)"
                            allowClear
                        />
                    </FieldGroup>

                    <FieldGroup
                        label="Tags to Add"
                        description="Add tags to tickets after the AI agent responds. You can also select enrichments to add as tags."
                    >
                        <TagInput
                            tags={localConfig.tagsToAdd || []}
                            onAddTag={handleAddTag}
                            onRemoveTag={handleRemoveTag}
                            enrichmentOptions={enrichmentOptions}
                        />
                    </FieldGroup>

                    <FieldGroup
                        label="Custom Fields"
                        description="Set custom field values after the AI agent responds."
                    >
                        <Flex direction="column" gap="3" width="100%">
                            {localConfig.customFields?.map((field, index) => (
                                <CustomFieldRow
                                    key={`${field.fieldId || "empty"}-${index}`}
                                    index={index}
                                    fieldId={field.fieldId}
                                    value={field.value}
                                    availableFields={availableCustomFields}
                                    fieldReferenceOptions={fieldReferenceOptions}
                                    currentField={getCustomFieldById(field.fieldId)}
                                    onUpdate={handleUpdateCustomField}
                                    onRemove={handleRemoveCustomField}
                                />
                            ))}

                            <Button
                                variant="soft"
                                color="gray"
                                onClick={handleAddCustomField}
                                highContrast
                                disabled={
                                    localConfig.customFields?.some(
                                        (field) => !field.fieldId || field.fieldId === ""
                                    ) || false
                                }
                            >
                                <PlusIcon size={16} />
                                Add Custom Field
                            </Button>
                        </Flex>
                    </FieldGroup>
                </Box>
            </Flex>
        </Flex>
    );
}
